import { createContext, useContext } from 'react';
import {
  satDiagnosticApi,
  satPlanApi,
  satPracticeApi,
  satProfileApi,
  SatDiagnosticAssignmentResponse,
  SatDomain,
  SatPracticeSetResponse,
  SatStudentProfileOverviewDto,
  SatStudyPlanDto,
} from '@/api/sat';

/**
 * The seam that lets ONE dashboard (`SatMyJourneyTab`) run off two different
 * data sources.
 *
 * Authed students read the four `/sat/me/**` endpoints and play at
 * `/sat/diagnostic/:id`. Anonymous visitors on `/what-we-do/sat` read
 * browser-held progress and play somewhere else entirely — but they see the
 * SAME course home, not a marketing mock of it (specs/009).
 *
 * Everything that differs between the two lives here: where the data comes
 * from, where the CTAs point, what the plan controls do, and whose name goes
 * in the hero. The component itself knows about none of it.
 */

/** Where the journey's CTAs point. Authed and open modes route differently. */
export interface SatJourneyPaths {
  diagnostic(id: string): string;
  diagnosticResult(id: string): string;
  practice(id: string): string;
  practiceResult(id: string): string;
}

/** The routes the logged-in player has always used. */
export const AUTHED_PATHS: SatJourneyPaths = {
  diagnostic: (id) => `/sat/diagnostic/${id}`,
  diagnosticResult: (id) => `/sat/diagnostic/${id}/result`,
  practice: (id) => `/sat/practice/${id}`,
  practiceResult: (id) => `/sat/practice/${id}/result`,
};

export interface SatJourneySource {
  mode: 'authed' | 'open';
  getOverview(focusDomain: SatDomain | null): Promise<SatStudentProfileOverviewDto>;
  getPlan(): Promise<SatStudyPlanDto>;
  getAssignments(): Promise<SatDiagnosticAssignmentResponse[]>;
  getPracticeSets(): Promise<SatPracticeSetResponse[]>;
  paths: SatJourneyPaths;
  /**
   * What the plan controls do. `'wizard'` opens the real plan wizard / score
   * dialog; `'invite'` (anonymous) opens the account-invite dialog instead —
   * there is no server-side plan to save to yet.
   */
  planEditing: 'wizard' | 'invite';
  /**
   * Hero greeting name. Anonymous sources have none; the authed source leaves
   * this null on purpose — it is a plain object with no hooks, so the
   * component reads `useAuth()` itself when `mode === 'authed'`.
   */
  firstName: string | null;
}

/**
 * The default source: exactly the four calls `SatMyJourneyTab` has always
 * made. No hooks — it is a module-level constant so `useSatJourneySource()`
 * can hand it back without a provider anywhere in the tree.
 */
export const authedJourneySource: SatJourneySource = {
  mode: 'authed',
  getOverview: (focusDomain) => satProfileApi.getMyOverview({ activeDomain: focusDomain }),
  getPlan: () => satPlanApi.getMyPlan(),
  getAssignments: () => satDiagnosticApi.getMyAssignments(),
  getPracticeSets: () => satPracticeApi.getMyPracticeSets(),
  paths: AUTHED_PATHS,
  planEditing: 'wizard',
  firstName: null,
};

export const SatJourneySourceContext = createContext<SatJourneySource | null>(null);

/**
 * The journey's data source. Unprovided means authed — so every existing
 * mount of the dashboard keeps working with no wrapper at all.
 */
export function useSatJourneySource(): SatJourneySource {
  return useContext(SatJourneySourceContext) ?? authedJourneySource;
}
